import React from 'react';
import { createPortal } from 'react-dom';
import { IoChevronBackOutline, IoFilterOutline } from 'react-icons/io5';
import { User } from '../../types';
import CallButtons from './CallButtons';
import DatePicker from './DatePicker';
import { formatLastSeen } from '../../utils/dateFormat';

interface ChatHeaderProps {
  otherUser: User;
  onVideoCall: () => void;
  onAudioCall: () => void;
  selectedDate: string | null;
  onDateChange: (date: string | null) => void;
  isTyping?: boolean;
  isRecording?: boolean;
  onBackToFriends?: () => void;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({ 
  otherUser, 
  onVideoCall, 
  onAudioCall, 
  selectedDate, 
  onDateChange,
  isTyping = false,
  isRecording = false,
  onBackToFriends
}) => {
  const [showDatePicker, setShowDatePicker] = React.useState(false);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [showDatePicker]);

  const handleDateChange = (date: string | null) => {
    onDateChange(date);
    setShowDatePicker(false);
  };

  const getStatusText = () => {
    if (isRecording) {
      return (
        <span className="flex items-center gap-2 text-red-400">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          Recording voice...
        </span>
      );
    }
    if (isTyping) {
      return (
        <span className="flex items-center gap-2 text-[#06B6D4]">
          <span className="flex gap-0.5">
            <span className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
            <span className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
            <span className="w-1.5 h-1.5 bg-[#06B6D4] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
          </span>
          Typing...
        </span>
      );
    }
    if (otherUser.isOnline) {
      return (
        <span className="flex items-center gap-2">
          <span className="w-2 h-2 bg-[#06B6D4] rounded-full"></span>
          Online
        </span>
      );
    }
    return `Last seen: ${formatLastSeen(otherUser.lastSeen)}`;
  };

  return (
    <div className="bg-[#09090B]/95 border-b border-[#7C3AED]/20 shadow-lg shadow-[#7C3AED]/10 flex-shrink-0 backdrop-blur">
      <div className="p-3 sm:p-4">
        {/* Main header row */}
        <div className="flex items-center justify-between gap-3">
          {/* Left: Back button + User info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {onBackToFriends && (
              <button
                onClick={onBackToFriends}
                className="p-2 hover:bg-[#7C3AED]/15 rounded-xl transition-all active:scale-95 flex-shrink-0 text-[#A1A1AA] hover:text-white"
                title="Back to friends"
              >
                <IoChevronBackOutline size={20} />
              </button>
            )}
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-white truncate">{otherUser.name}</h2>
              <p className="text-xs sm:text-sm text-[#A1A1AA] truncate">
                {getStatusText()}
              </p>
            </div>
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* Filter/Date picker button - always visible */}
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`p-2 hover:bg-[#7C3AED]/15 rounded-xl transition-all active:scale-95 ${
                  selectedDate 
                    ? 'text-[#06B6D4] bg-[#06B6D4]/15' 
                    : 'text-[#A1A1AA] hover:text-white'
                }`}
                title={selectedDate ? 'Filter active - Click to change date' : 'Filter messages by date'}
              >
                <IoFilterOutline size={22} />
              </button>
              
              {showDatePicker &&
                createPortal(
                  <>
                    <div
                      className="fixed inset-0 z-[9000] bg-black/35"
                      onClick={() => setShowDatePicker(false)}
                    />
                    <div className="fixed right-3 top-[4.75rem] z-[9010] sm:right-6">
                      <DatePicker
                        value={selectedDate}
                        onChange={handleDateChange}
                      />
                    </div>
                  </>,
                  document.body
                )}
            </div>
            
            {/* Call buttons - always visible */}
            <CallButtons onVideoCall={onVideoCall} onAudioCall={onAudioCall} />
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
